import { NAVIGO_STATUS, STATUS_LABEL, selectableStatusesFor } from "./appointmentConstants";

/**
 * The dashboard's column filters — the option lists, and the rules for turning
 * a selection into the request body POST /v1/appointment/filter expects.
 *
 * Every filter here is applied SERVER-side. Nothing is filtered on the loaded
 * page: the list is ~9k rows at 20 a page, so a client-side filter would only
 * ever search the visible slice and quietly lie about the rest.
 *
 * The key names are declared once, here, because the server binds the body with
 * `c.Bind` and SILENTLY DROPS keys it doesn't recognise — a typo'd filter name
 * fails open and returns unfiltered data instead of erroring.
 */

/** Body keys, so a filter name is never spelled twice. */
export const FILTER_KEY = Object.freeze({
  status: "status",
  appointmentDate: "appointmentDate",
  service: "serviceID",
  accountCode: "accountCode",
  // The appointments filter takes an array of profile types; an empty selection
  // must DROP the key rather than send [], which applyFilter already does for
  // every empty array. Same contract bridge uses (NavigoDashboard.js:772).
  userType: "profileTypes",
});

/**
 * Status filter options for WHO IS SIGNED IN (Nittin, 2026-09-17): EXACTLY the
 * per-role list the row's status dropdown offers (selectableStatusesFor — a
 * nutritionist's or medical-team member's has Successful HRA / PRC, a
 * doctor's, physio's or psychotherapist's Completed), in its order. It used to
 * be every consultation status for everyone. Nothing is added: the product's
 * list has no Reschedule requested and no No show.
 *
 * One option per LABEL, sending every code that wears it: "Scheduled" is a
 * legacy 0 and the current 5, rows carry both in the wild, and sending one
 * would hide half the scheduled appointments.
 */
export const statusOptionsFor = (doctorCategory) => {
  const offered = selectableStatusesFor(doctorCategory);
  const seen = {};
  const out = [];
  offered.forEach((code) => {
    const label = STATUS_LABEL[code];
    if (!label || seen[label]) return;
    seen[label] = true;
    const codes = Object.keys(STATUS_LABEL)
      .map(Number)
      .filter((c) => STATUS_LABEL[c] === label);
    out.push({ id: label, label, codes });
  });
  return Object.freeze(out);
};

/** Sanity: the two Scheduled ids really did collapse into one option. */
export const SCHEDULED_CODES = [NAVIGO_STATUS.SCHEDULED_LEGACY, NAVIGO_STATUS.SCHEDULED];


/**
 * A local calendar day as the UTC instant the server compares against.
 *
 * `startOf("day")` in the BROWSER's zone, then to UTC — not bridge's
 * `.utc().startOf("day").add(1, "day")`, which is an IST-specific correction:
 * it cancels the rollback that converting IST-midnight to UTC causes, and lands
 * a day out anywhere at or west of Greenwich.
 */
export const dayStartIso = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
};

export const dayEndIso = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
};

/**
 * Merge one column's selection into the filter object.
 *
 * Two rules the server enforces and one it does not:
 *  - an unset filter is ABSENT, never `null` and never `[]`. An empty array is
 *    a filter matching nothing, not a cleared filter.
 *  - any change resets to page 1. Without it, filtering while on page 12 asks
 *    for page 12 of a shorter list and shows an empty table.
 *  - `from > to` on a date range is a 400, so it is blocked in the UI instead.
 */
export const applyFilter = (prev, key, value) => {
  const { [key]: _omit, ...rest } = prev;
  const empty =
    value == null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" && !Array.isArray(value) && !value.from && !value.to);
  return empty ? { ...rest, pageNum: 1 } : { ...rest, [key]: value, pageNum: 1 };
};

